/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, breakpoints, useMediaQuery, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useEventingMetadata } from 'bundles/unified-career-academy/contexts/EventingMetadataContext';

type Props = {
  title?: string | null;
  description?: string | null;
  footnotes?: string | null;
  skills?: React.ReactNode;
  jobTitles?: React.ReactNode;
  addExtraPadding: boolean;
};

const styles = {
  section: (_: Theme, addExtraPadding: boolean) => {
    const padding = addExtraPadding ? `var(--cds-spacing-1400) 0 var(--cds-spacing-1000)` : `var(--cds-spacing-1000) 0`;
    const mobilePadding = addExtraPadding
      ? `144px 0 var(--cds-spacing-300)`
      : `var(--cds-spacing-1000) 0 var(--cds-spacing-300)`;
    return css`
      background-color: var(--cds-color-grey-25);
      padding: ${padding};
      ${breakpoints.down('xs')} {
        padding: ${mobilePadding};
      }
    `;
  },
  title: css`
    padding-bottom: 4px;
  `,
  footnotes: css`
    padding-top: 16px;
  `,
};

const RoleDescriptionModule = ({ title, description, footnotes, skills, jobTitles, addExtraPadding }: Props) => {
  const isSingleColumn = (!skills && !jobTitles) || (!title && !description);
  const eventingMetadata = useEventingMetadata();
  const theme = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  const pageContent = React.useMemo(() => {
    const SkillsAndJobsComponent = (
      <Grid key="skills-jobs-component" item xs={12} sm={isSingleColumn ? 12 : 4}>
        {skills}
        {jobTitles}
      </Grid>
    );

    const TitleAndDescriptionComponent =
      skills || jobTitles ? (
        <Grid key="title-description-component" item xs={12} sm={isSingleColumn ? 12 : 8}>
          {title && (
            <Typography variant="h1semibold" component="h2" css={styles.title}>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="h2" component="p">
              {description}
            </Typography>
          )}
          {footnotes && <Markdown source={`${footnotes}`} css={styles.footnotes} />}
        </Grid>
      ) : null;

    return isMobile
      ? [SkillsAndJobsComponent, TitleAndDescriptionComponent]
      : [TitleAndDescriptionComponent, SkillsAndJobsComponent];
  }, [skills, jobTitles, isMobile, description, title, footnotes, isSingleColumn]);

  return (
    <TrackedDiv
      trackingName="rdp_description_section"
      trackClicks={false}
      withVisibilityTracking
      css={styles.section(theme, addExtraPadding)}
      data={{ title, ...eventingMetadata }}
    >
      <PageGridContainer>
        <Grid container item spacing={48} xs={12}>
          {pageContent
            .filter((Component) => !!Component)
            .map((Component) => (
              <React.Fragment key={Component.key}>{Component}</React.Fragment>
            ))}
        </Grid>
      </PageGridContainer>
    </TrackedDiv>
  );
};

export default RoleDescriptionModule;
